import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  // Declare private fields at class level
  #gridChangeHandler;
  #gridClickHandler;
  #gridControlsObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Use document-level event delegation since grid controls may be outside results-list
    // Listen for change events on grid controls anywhere in the document
    this.#gridChangeHandler = (event) => {
      const target = event.target;
      
      if (target instanceof HTMLInputElement && (target.name === 'grid-mobile' || target.name === 'grid')) {
        // Only handle if this results-list contains the product grid
        const grid = this.refs?.grid || this.querySelector('.product-grid');
        if (grid) {
          event.stopPropagation(); // Prevent duplicate handling
          this.updateLayout(event);
        }
      }
    };
    
    // Listen for click events on grid controls (fallback for touch devices)
    this.#gridClickHandler = (event) => {
      const target = event.target;
      
      // Check if click is on a grid control input, label, icon, or SVG
      const label = target.closest('label.column-options__option');
      let gridInput = null;
      
      if (label) {
        gridInput = label.querySelector('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]');
      } else if (target instanceof HTMLInputElement && (target.name === 'grid-mobile' || target.name === 'grid')) {
        gridInput = target;
      } else if (target.closest('.column-options-wrapper')) {
        // Click might be on wrapper, find the closest input
        const wrapper = target.closest('.column-options-wrapper');
        const clickedOption = target.closest('.column-options__option') || 
          (target.closest('.column-picker')?.parentElement);
        if (clickedOption) {
          gridInput = clickedOption.querySelector('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]');
        }
      }
      
      if (gridInput instanceof HTMLInputElement) {
        // Only handle if this results-list contains the product grid
        const grid = this.refs?.grid || this.querySelector('.product-grid');
        if (grid) {
          // Prevent default to avoid any form submission
          event.preventDefault();
          event.stopPropagation();
          
          // Update all radio buttons in the same group first
          const name = gridInput.name;
          const value = gridInput.value;
          this.#updateRadioButtonStates(name, value);
          
          // Ensure this input is checked
          gridInput.checked = true;
          
          // Dispatch a proper change event
          const changeEvent = new Event('change', { bubbles: true, cancelable: true });
          gridInput.dispatchEvent(changeEvent);
        }
      }
    };
    
    // Attach document-level listeners
    document.addEventListener('change', this.#gridChangeHandler, true);
    document.addEventListener('click', this.#gridClickHandler, true);
    
    // Also attach listeners directly to existing grid controls
    this.#attachGridControlListeners();
    
    // Restore grid view from sessionStorage after re-render
    this.#restoreGridView();
    
    // Listen for section load events to restore grid view after section morphs
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
    
    // Watch for dynamically added grid controls (after section re-renders)
    this.#watchForGridControls();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    
    // Remove document-level event listeners
    if (this.#gridChangeHandler) {
      document.removeEventListener('change', this.#gridChangeHandler, true);
    }
    if (this.#gridClickHandler) {
      document.removeEventListener('click', this.#gridClickHandler, true);
    }
    
    // Disconnect MutationObserver if it exists
    if (this.#gridControlsObserver) {
      this.#gridControlsObserver.disconnect();
      this.#gridControlsObserver = null;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    // Support both desktop and mobile grid controls
    let value = target.value;
    if (target.name === 'grid-mobile') {
      // For mobile, set the product-grid-view to 'mobile-single' or 'default'
      // Update all radio buttons in the same group to ensure correct visual state
      this.#updateRadioButtonStates('grid-mobile', value);
      this.#animateLayoutChange(value);
    } else if (target.name === 'grid') {
      // For desktop, set the product-grid-view as usual
      // Update all radio buttons in the same group to ensure correct visual state
      this.#updateRadioButtonStates('grid', value);
      this.#animateLayoutChange(value);
    }
  }

  /**
   * Updates the checked state of all radio buttons in a group
   * @param {string} name - The name attribute of the radio group
   * @param {string} value - The value of the radio button to check
   */
  #updateRadioButtonStates(name, value) {
    const allInputs = document.querySelectorAll(`input[type="radio"][name="${name}"]`);
    allInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === value;
      }
    });
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    let grid = this.refs && this.refs.grid;
    if (!grid) {
      const el = this.querySelector('.product-grid');
      if (el instanceof HTMLUListElement) grid = el;
    }
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    let grid = this.refs && this.refs.grid;
    if (!grid) {
      const el = this.querySelector('.product-grid');
      if (el instanceof HTMLUListElement) grid = el;
    }
    if (!grid) {
      return;
    }
    grid.setAttribute('product-grid-view', value);
    
    // Ensure radio button states are synchronized after layout change
    const currentDevice = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    if (currentDevice === 'desktop') {
      this.#updateRadioButtonStates('grid', value);
    } else {
      this.#updateRadioButtonStates('grid-mobile', value);
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    // Search entire document for grid controls
    const targetElement = event.matches
      ? document.querySelector('[data-grid-layout="desktop-default-option"]')
      : document.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Handles section load events to restore grid view after section morphs
   */
  #handleSectionLoad = (event) => {
    // Only handle if it's this section
    if (event.detail?.sectionId === this.sectionId) {
      // Restore grid view after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.#restoreGridView();
      }, 100);
    }
  };

  /**
   * Attaches event listeners directly to grid controls
   */
  #attachGridControlListeners() {
    // Find all grid controls in the document (they may be outside results-list)
    const gridInputs = document.querySelectorAll('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]');
    gridInputs.forEach((input) => {
      if (input instanceof HTMLInputElement && !input.dataset.gridListenerAttached) {
        // Mark as having listener attached to avoid duplicates
        input.dataset.gridListenerAttached = 'true';
        
        // Add change listener
        input.addEventListener('change', (event) => {
          const grid = this.refs?.grid || this.querySelector('.product-grid');
          if (grid && event.target === input) {
            this.updateLayout(event);
          }
        });
      }
    });
  }

  /**
   * Watches for dynamically added grid controls and ensures they work
   */
  #watchForGridControls() {
    if (!window.MutationObserver) return;
    
    // Disconnect existing observer if any
    if (this.#gridControlsObserver) {
      this.#gridControlsObserver.disconnect();
    }
    
    this.#gridControlsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if grid controls were added
            const gridInputs = node.matches?.('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]')
              ? [node]
              : node.querySelectorAll?.('input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]') || [];
            
            // Re-attach listeners to new inputs
            if (gridInputs.length > 0) {
              this.#attachGridControlListeners();
            }
          }
        });
      });
    });
    
    // Observe the entire document for changes (since grid controls may be outside results-list)
    this.#gridControlsObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Restores the grid view from sessionStorage after section re-render
   */
  #restoreGridView() {
    requestAnimationFrame(() => {
      const grid = this.refs?.grid || this.querySelector('.product-grid');
      if (!grid) return;

      const currentDevice = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      const storedLayout = sessionStorage.getItem(`product-grid-view-${currentDevice}`);

      if (storedLayout) {
        // Set the grid view attribute
        grid.setAttribute('product-grid-view', storedLayout);

        // Update the correct radio buttons based on current device (search entire document)
        if (currentDevice === 'desktop') {
          const desktopOptions = document.querySelectorAll('input[type="radio"][name="grid"]');
          desktopOptions.forEach((option) => {
            if (option instanceof HTMLInputElement) {
              option.checked = option.value === storedLayout;
            }
          });
        } else {
          const mobileOptions = document.querySelectorAll('input[type="radio"][name="grid-mobile"]');
          mobileOptions.forEach((option) => {
            if (option instanceof HTMLInputElement) {
              option.checked = option.value === storedLayout;
            }
          });
        }
      }
    });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
